import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useEffect, useRef, useState } from "react";
import nodocsImg from "../../images/noDocs.png";
import myDocsStyles from "./myDocs.module.css";
import { getPrintDate } from "../../utils/utils";
import Modal from "../modals/modal/Modal";
import ScannedDocumentPopup from "../modals/scannedDocument_success_popup/scannedDocument_success_popup";
import {
  useFetchDocsQuery,
  useSendFileMutation,
} from "../../services/api/user.api";
import {
  applicationDetails,
  docsCategories,
  sendFileFormInitialValue,
  urgencyVariants,
} from "../../utils/constants";
import { useAppSelector } from "../../services/hooks";
import { userSelector } from "../../services/selectors/user.selectors";
import Loader from "../loader/loader";
import GeneralErrorPopup from "../modals/errorPopup/errorPopup";
import { IUploadedFiles, TSendFileForm } from "../../types/types";
import { useContactAccess } from "../../utils/contactAccess";
import i18n from "../../i18n";

function MyDocs() {
  const [openModalScan, setOpenModalScan] = useState(false);
  const [toggle, setToggle] = useState<Record<number, boolean>>({ 1000: true });
  const [showScanner, setShowScanner] = useState(false);
  const [form, setForm] = useState<TSendFileForm>(sendFileFormInitialValue);
  const [tempFiles, setTempFiles] = useState<File[]>([]);
  const [formDetails, setFormDetails] = useState<IUploadedFiles>({
    id: -1,
    usersId: parseInt(useAppSelector(userSelector).id),
    fileName: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const user = useAppSelector(userSelector);
  const {
    data: docs,
    isError: isFetchdocsError,
    isLoading: isLoadingdocs,
    refetch,
  } = useFetchDocsQuery(user.guid);
  const [sendFile] = useSendFileMutation();
  // A contact allowed only to view documents gets the list without the upload form.
  const { documentsAction } = useContactAccess();

  const isDisabled =
    !formDetails.urgency ||
    tempFiles.length === 0 ||
    !formDetails.subject ||
    !formDetails.details;
  const toggleAnswer = (id: number) => {
    setToggle((prev) => ({
      ...prev,
      [id]: !prev[id as keyof typeof prev],
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setTempFiles(Array.from(files));
    }
  };


  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmittingRef.current || tempFiles.length === 0) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("urgency", String(formDetails.urgency));
    formData.append("subject", formDetails.subject ?? "");
    formData.append("details", formDetails.details ?? "");
    tempFiles.forEach((file) => formData.append("file", file));

    try {
      await sendFile({ UsersGuid: user.guid, formData }).unwrap();
      setTempFiles([]);
      setOpenModalScan(true);
      refetch();
      setForm(sendFileFormInitialValue);
      setFormDetails({
        id: -1,
        usersId: parseInt(user.id),
        fileName: "",
      });
    } catch (error) {
      console.error(t('UPLOAD_ERROR'), error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (isLoadingdocs) return <Loader />;
  if (isFetchdocsError) return <GeneralErrorPopup onClose={() => { }} />;

  return (
    <section className={myDocsStyles.section} dir={i18n.dir()}>
      {openModalScan && (
        <Modal onClose={() => setOpenModalScan(false)}>
          <ScannedDocumentPopup onClose={() => setOpenModalScan(false)} />
        </Modal>
      )}
      <h2 className={myDocsStyles.section__title}>{t('DOCUMENTS')}</h2>
      {docs?.length === 0 && !showScanner ? (
        <>
          <img src={nodocsImg} alt={t('PRESCRIPTIONS')} className={myDocsStyles.img} />
          <p className={myDocsStyles.noDocs}>{t('NO_DOCUMENTS_FOUND')}</p>
          {documentsAction && (
          <button
            type="button"
            onClick={() => setShowScanner(true)}
            className={`button hover ${myDocsStyles.nextBtn}`}
          >
            {t('UPLOAD_NEW_DOCUMENT')}</button>
          )}
        </>
      ) : (
        <ul className={myDocsStyles.list}>
          {documentsAction && (
          <li
            className={`${myDocsStyles.li} ${toggle[1000] ? myDocsStyles.li_long : ""
              }`}
            onClick={() => toggleAnswer(1000)}
          >
            <p className={myDocsStyles.title}>{t('UPLOAD_DOCUMENTS')}</p>
            <div className={myDocsStyles.scanSection}>
              <p className={myDocsStyles.scanTitle}>
                {t('DOCUMENT_SCAN')}<span className={myDocsStyles.scanIcon} />
              </p>
              <div className={myDocsStyles.scanOption}>
                <form className={myDocsStyles.form} onSubmit={submitForm}>
                  <div
                    className={`button button_white ${myDocsStyles.btn} ${myDocsStyles.category
                      } ${toggle[999] && myDocsStyles.category_active}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAnswer(999);
                    }}
                  >
                    {t(form.category.title)}
                    <ul className={myDocsStyles.variants}>
                      {docsCategories.map(
                        (c) =>
                          c.value && (
                            <li
                              key={c.value}
                              className={myDocsStyles.variant}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormDetails((prev) => ({
                                  ...prev,
                                  subject: t(c.title),
                                }));
                                setForm((prev) => ({
                                  ...prev,
                                  category: c,
                                }));
                                toggleAnswer(999);
                              }}
                            >
                              {t(c.title)}
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                  <div
                    className={`button button_white ${myDocsStyles.btn} ${myDocsStyles.category
                      } ${toggle[998] && myDocsStyles.category_active}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAnswer(998);
                    }}
                  >
                    {t(form.details?.title) || t('DETAILS')}
                    <ul className={myDocsStyles.variants}>
                      {applicationDetails.map(
                        (c) =>
                          c.value && (
                            <li
                              key={c.value}
                              className={myDocsStyles.variant}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormDetails((prev) => ({
                                  ...prev,
                                  details: t(c.title),
                                }));
                                setForm((prev) => ({
                                  ...prev,
                                  details: c,
                                }));
                                toggleAnswer(998);
                              }}
                            >
                              {t(c.title)}
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                  <div
                    className={`button button_white ${myDocsStyles.btn} ${myDocsStyles.category
                      } ${toggle[997] && myDocsStyles.category_active}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAnswer(997);
                    }}
                  >
                    {t(form.urgent.title)}
                    <ul className={myDocsStyles.variants}>
                      {urgencyVariants.map(
                        (c) =>
                          c.value && (
                            <li
                              key={c.value}
                              className={myDocsStyles.variant}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormDetails((prev) => ({
                                  ...prev,
                                  urgency: parseInt(c.value),
                                }));
                                setForm((prev) => ({
                                  ...prev,
                                  urgent: c,
                                }));
                                toggleAnswer(997);
                              }}
                            >
                              {t(c.title)}
                            </li>
                          )
                      )}
                    </ul>
                  </div>

                  <label
                    htmlFor="icon-button-file"
                    className={`button button_white ${myDocsStyles.btn} hover`}
                  >
                    {tempFiles.length > 0
                      ? `נבחרו ${tempFiles.length} קבצים`
                      : t('DOCUMENT_SCAN_1')}
                  </label>
                  <input
                    onChange={handleFileChange}
                    className={myDocsStyles.input}
                    accept="image/*,.pdf"
                    id="icon-button-file"
                    type="file"
                    capture="environment"
                    name="file"
                    multiple
                  />

                  <button
                    type="submit"
                    className={`button ${myDocsStyles.btn} ${(isDisabled || isSubmitting) &&
                      myDocsStyles.btn_disabled
                      }`}
                    disabled={isDisabled || isSubmitting}
                  >
                    {isSubmitting ? t('SENDING_MESSAGE') : t('SEND_COMMAND')}
                  </button>
                </form>
              </div>
            </div>
          </li>
          )}
          <li
            className={`${myDocsStyles.li} ${toggle[1001] ? myDocsStyles.li_long : ""
              }`}
            onClick={() => toggleAnswer(1001)}
          >
            <p className={myDocsStyles.title}>{t('DOCUMENTS_UPLOADED')}</p>
            <div className={myDocsStyles.scanSection}>
              {docs?.length ? (
                <ul className={myDocsStyles.scannedList}>
                  {docs.map((d, index) => (
                    <li key={index} className={myDocsStyles.scannedLi}>
                      <p className={myDocsStyles.label}>
                        {t('DOCUMENT_NAME')}<span className={myDocsStyles.info}>
                          {d.fileName.split("/").pop()}
                        </span>
                      </p>
                      <p className={myDocsStyles.label}>
                        {t('UPLOAD_DATE')}<span className={myDocsStyles.info}>
                          {d.dateUploaded
                            ? getPrintDate(new Date(d.dateUploaded))
                            : "—"}
                        </span>
                      </p>
                      <p className={myDocsStyles.label}>
                        {t('STATUS')}<span className={myDocsStyles.info}>
                          {d.statusId === 0 ? t('IN_TREATMENT') : t('TUPAL')}
                        </span>
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={myDocsStyles.text}>{t('DOCUMENTS_NOT_YET_UPLOADED')}</p>
              )}
            </div>
          </li>
        </ul>
      )}
    </section>
  );
}

export default MyDocs;